import { useQuery } from "@tanstack/react-query";
import { isMock, getJson } from "./client";
import type { AlertLevel, Segment } from "./types";
import {
  PSI_WARN,
  PSI_CRIT,
  CSI_WARN,
  CSI_CRIT,
  classifyBand,
  normalizeStatus,
} from "../classify";

export type StabilityStatus = AlertLevel | "SKIPPED";
// UP/DOWN = month-over-month movement of the CSI value (latest vs previous month).
// MORE/LESS_PREDICTIVE are legacy values, no longer produced.
export type CsiDirection =
  | "UP"
  | "DOWN"
  | "STABLE"
  | "MORE_PREDICTIVE"
  | "LESS_PREDICTIVE";

/** Direction from the last two non-null trend points (latest vs previous month). */
export function computeDirection(
  trend: { cohort: string; value: number | null }[],
): CsiDirection | undefined {
  const vals = trend.filter((t) => t.value !== null);
  if (vals.length < 2) return undefined;
  const latest = vals[vals.length - 1].value as number;
  const prev = vals[vals.length - 2].value as number;
  const d = latest - prev;
  return d > 0 ? "UP" : d < 0 ? "DOWN" : "STABLE";
}

export interface FeatureStabilityRow {
  feature: string;
  featureKey: string;
  displayName: string;
  displayOrder: number;
  featureType: "Loan Features" | "Macro Features";
  type: "numeric" | "categorical";
  value: number | null;
  status: StabilityStatus;
  direction?: CsiDirection;
  trend: { cohort: string; value: number | null }[];
}

export interface InputStabilityResponse {
  segment: Segment;
  metric: "PSI" | "CSI";
  aggregateFlag: {
    fired: boolean;
    warnFeatureCount: number;
    featuresInWarn: string[];
  };
  rows: FeatureStabilityRow[];
  notes: string[];
  cohortRange: string;
}

export { PSI_WARN, PSI_CRIT, CSI_WARN, CSI_CRIT };

const PSI_COHORTS = [
  "2024-02", "2024-03", "2024-04", "2024-05", "2024-06", "2024-07",
  "2024-08", "2024-09", "2024-10", "2024-11", "2024-12", "2025-01",
  "2025-02", "2025-03", "2025-04", "2025-05", "2025-06",
];

const ASSET_TYPE_TREND = [
  0.592, 0.557, 0.519, 0.663, 0.663, 0.551, 0.676, 0.626, 0.742,
  0.724, 0.66, 0.629, 0.627, 0.655, 0.78, 0.868, 1.308,
];

function flatTrend(baseline: number, seed: number): number[] {
  const out: number[] = [];
  let s = seed;
  for (let i = 0; i < PSI_COHORTS.length; i++) {
    s = (s * 9301 + 49297) % 233280;
    const noise = (s / 233280 - 0.5) * baseline * 0.6;
    out.push(Math.max(0, +(baseline + noise).toFixed(4)));
  }
  return out;
}

function mkTrend(values: number[]): { cohort: string; value: number | null }[] {
  return PSI_COHORTS.map((c, i) => ({ cohort: c, value: values[i] ?? null }));
}

const PSI_OVERALL_ROWS: FeatureStabilityRow[] = [
  { feature: "Asset Type", featureKey: "asset_type", displayName: "Asset Type", displayOrder: 1, featureType: "Loan Features", type: "categorical", value: 1.3079, status: "CRITICAL", trend: mkTrend(ASSET_TYPE_TREND) },
  { feature: "# Trade Experiences", featureKey: "trade_exp", displayName: "# Trade Experiences", displayOrder: 2, featureType: "Loan Features", type: "numeric", value: 0.0877, status: "STABLE", trend: mkTrend(flatTrend(0.07, 11)) },
  { feature: "PN Master Score", featureKey: "pn_master", displayName: "PN Master Score", displayOrder: 3, featureType: "Loan Features", type: "numeric", value: 0.0555, status: "STABLE", trend: mkTrend(flatTrend(0.05, 22)) },
  { feature: "Avg High Credit", featureKey: "avg_high_credit", displayName: "Avg High Credit", displayOrder: 4, featureType: "Loan Features", type: "numeric", value: 0.0257, status: "STABLE", trend: mkTrend(flatTrend(0.025, 33)) },
  { feature: "Years in Business", featureKey: "yib", displayName: "Years in Business", displayOrder: 5, featureType: "Loan Features", type: "numeric", value: 0.0255, status: "STABLE", trend: mkTrend(flatTrend(0.025, 44)) },
  { feature: "# Employees", featureKey: "employees", displayName: "# Employees", displayOrder: 6, featureType: "Loan Features", type: "numeric", value: 0.0236, status: "STABLE", trend: mkTrend(flatTrend(0.022, 55)) },
  { feature: "Loan Amount", featureKey: "loan_amount", displayName: "Loan Amount", displayOrder: 7, featureType: "Loan Features", type: "numeric", value: 0.0224, status: "STABLE", trend: mkTrend(flatTrend(0.021, 66)) },
  { feature: "FICO (avg)", featureKey: "fico_avg", displayName: "FICO (avg)", displayOrder: 8, featureType: "Loan Features", type: "numeric", value: 0.0217, status: "STABLE", trend: mkTrend(flatTrend(0.02, 77)) },
  { feature: "Commercial Credit Score", featureKey: "ccs", displayName: "Commercial Credit Score", displayOrder: 9, featureType: "Loan Features", type: "numeric", value: 0.021, status: "STABLE", trend: mkTrend(flatTrend(0.02, 88)) },
  { feature: "# Inquiries", featureKey: "inquiries", displayName: "# Inquiries", displayOrder: 10, featureType: "Loan Features", type: "numeric", value: 0.0113, status: "STABLE", trend: mkTrend(flatTrend(0.012, 99)) },
  { feature: "Past Delinquency Count", featureKey: "past_delinq", displayName: "Past Delinquency Count", displayOrder: 11, featureType: "Loan Features", type: "numeric", value: 0.001, status: "STABLE", trend: mkTrend(flatTrend(0.002, 111)) },
  { feature: "Paydex", featureKey: "paydex", displayName: "Paydex", displayOrder: 12, featureType: "Loan Features", type: "numeric", value: 0.0007, status: "STABLE", trend: mkTrend(flatTrend(0.001, 122)) },
  { feature: "# Delinquencies", featureKey: "delinq", displayName: "# Delinquencies", displayOrder: 13, featureType: "Loan Features", type: "numeric", value: 0.0, status: "STABLE", trend: mkTrend(flatTrend(0.0005, 133)) },
];

const PSI_NOTES = [
  "Series begins February 2024. The January 2024 cohort is being backfilled.",
];

const CSI_COHORTS = [
  "2024-02", "2024-03", "2024-04", "2024-05",
  "2024-06", "2024-07", "2024-08", "2024-09",
];

function csiTrend(target: number, seed: number, drift = false): { cohort: string; value: number | null }[] {
  let s = seed;
  return CSI_COHORTS.map((c, i) => {
    s = (s * 9301 + 49297) % 233280;
    const noise = (s / 233280 - 0.5) * 0.02;
    const ramp = drift ? (i / (CSI_COHORTS.length - 1)) * target : target;
    const base = drift ? ramp : target;
    return { cohort: c, value: Math.max(0, +(base + noise).toFixed(4)) };
  });
}

const CSI_OVERALL_ROWS: FeatureStabilityRow[] = [
  { feature: "# Trade Experiences", featureKey: "trade_exp", displayName: "# Trade Experiences", displayOrder: 2, featureType: "Loan Features", type: "numeric", value: 0.18, status: "WARN", direction: "LESS_PREDICTIVE", trend: csiTrend(0.18, 11, true) },
  { feature: "Avg High Credit", featureKey: "avg_high_credit", displayName: "Avg High Credit", displayOrder: 4, featureType: "Loan Features", type: "numeric", value: 0.16, status: "WARN", direction: "LESS_PREDICTIVE", trend: csiTrend(0.16, 22, true) },
  { feature: "Commercial Credit Score", featureKey: "ccs", displayName: "Commercial Credit Score", displayOrder: 9, featureType: "Loan Features", type: "numeric", value: 0.14, status: "WARN", direction: "LESS_PREDICTIVE", trend: csiTrend(0.14, 33, true) },
  { feature: "Years in Business", featureKey: "yib", displayName: "Years in Business", displayOrder: 5, featureType: "Loan Features", type: "numeric", value: 0.12, status: "WARN", direction: "STABLE", trend: csiTrend(0.12, 44, true) },
  { feature: "Loan Amount", featureKey: "loan_amount", displayName: "Loan Amount", displayOrder: 7, featureType: "Loan Features", type: "numeric", value: 0.09, status: "STABLE", direction: "STABLE", trend: csiTrend(0.09, 55) },
  { feature: "PN Master Score", featureKey: "pn_master", displayName: "PN Master Score", displayOrder: 3, featureType: "Loan Features", type: "numeric", value: 0.08, status: "STABLE", direction: "STABLE", trend: csiTrend(0.08, 66) },
  { feature: "FICO (avg)", featureKey: "fico_avg", displayName: "FICO (avg)", displayOrder: 8, featureType: "Loan Features", type: "numeric", value: 0.04, status: "STABLE", direction: "STABLE", trend: csiTrend(0.04, 77) },
  { feature: "Paydex", featureKey: "paydex", displayName: "Paydex", displayOrder: 12, featureType: "Loan Features", type: "numeric", value: 0.03, status: "STABLE", direction: "STABLE", trend: csiTrend(0.03, 88) },
  { feature: "CPI Roll 3M", featureKey: "cpi_roll3m", displayName: "CPI Roll 3M", displayOrder: 100, featureType: "Macro Features", type: "numeric", value: 7.7985, status: "CRITICAL", direction: "LESS_PREDICTIVE", trend: csiTrend(7.7985, 100, true) },
  { feature: "Fed Funds Rate", featureKey: "fed_funds_rate", displayName: "Fed Funds Rate", displayOrder: 101, featureType: "Macro Features", type: "numeric", value: 6.2894, status: "CRITICAL", direction: "LESS_PREDICTIVE", trend: csiTrend(6.2894, 101, true) },
  { feature: "Unemployment Roll 3M", featureKey: "unemployment_roll3m", displayName: "Unemployment Roll 3M", displayOrder: 102, featureType: "Macro Features", type: "numeric", value: 6.1931, status: "CRITICAL", direction: "LESS_PREDICTIVE", trend: csiTrend(6.1931, 102, true) },
  { feature: "Indus. Prod Change 6M", featureKey: "industrialprod_change6m", displayName: "Indus. Prod Change 6M", displayOrder: 103, featureType: "Macro Features", type: "numeric", value: 4.6167, status: "CRITICAL", direction: "LESS_PREDICTIVE", trend: csiTrend(4.6167, 103, true) },
];

const CSI_NOTES = [
  "CSI is shown through September 2024. Values from October 2024 onward are under data-integrity review and are temporarily withheld.",
  "Commercial-credit features show weakening predictive relationship in the Corp segment since training.",
];

function bySegmentPsi(seg: Segment): FeatureStabilityRow[] {
  if (seg === "OVERALL") return PSI_OVERALL_ROWS;
  if (seg === "PG") {
    return PSI_OVERALL_ROWS.map((r) =>
      r.featureKey === "asset_type"
        ? { ...r, value: 0.84, status: "CRITICAL" as const, trend: mkTrend(ASSET_TYPE_TREND.map((v) => v * 0.7)) }
        : r,
    );
  }
  return PSI_OVERALL_ROWS.map((r) =>
    r.featureKey === "asset_type"
      ? { ...r, value: 1.62, status: "CRITICAL" as const, trend: mkTrend(ASSET_TYPE_TREND.map((v) => v * 1.2)) }
      : r,
  );
}

function bySegmentCsi(seg: Segment): { rows: FeatureStabilityRow[]; flag: InputStabilityResponse["aggregateFlag"] } {
  if (seg === "OVERALL") {
    return {
      rows: CSI_OVERALL_ROWS,
      flag: {
        fired: true,
        warnFeatureCount: 4,
        featuresInWarn: ["# Trade Experiences", "Avg High Credit", "Commercial Credit Score", "Years in Business"],
      },
    };
  }
  if (seg === "PG") {
    const rows = CSI_OVERALL_ROWS.map((r) =>
      r.featureKey === "avg_high_credit"
        ? { ...r, value: 0.11, status: "WARN" as const, direction: "LESS_PREDICTIVE" as const }
        : r.featureType === "Macro Features"
          ? r
          : { ...r, value: Math.min(0.08, (r.value ?? 0) * 0.5), status: "STABLE" as const, direction: "STABLE" as const },
    );
    return { rows, flag: { fired: false, warnFeatureCount: 1, featuresInWarn: ["Avg High Credit"] } };
  }
  const rows = CSI_OVERALL_ROWS.map((r) => {
    if (r.featureKey === "ccs") return { ...r, value: 0.22, status: "WARN" as const, direction: "LESS_PREDICTIVE" as const };
    if (r.featureKey === "trade_exp") return { ...r, value: 0.24, status: "WARN" as const, direction: "LESS_PREDICTIVE" as const };
    if (r.featureKey === "avg_high_credit") return { ...r, value: 0.2, status: "WARN" as const, direction: "LESS_PREDICTIVE" as const };
    return r;
  });
  return {
    rows,
    flag: {
      fired: true,
      warnFeatureCount: 5,
      featuresInWarn: ["# Trade Experiences", "Avg High Credit", "Commercial Credit Score", "Years in Business", "PN Master Score"],
    },
  };
}

export async function fetchStability(
  metric: "PSI" | "CSI",
  segment: Segment,
  modelId: number = 1,
): Promise<InputStabilityResponse> {
  const warn = metric === "PSI" ? PSI_WARN : CSI_WARN;
  const crit = metric === "PSI" ? PSI_CRIT : CSI_CRIT;

  if (!isMock) {
    const resp = await getJson<InputStabilityResponse>(
      `model-drift-monitoring/${metric.toLowerCase()}?model_id=${modelId}&segment=${segment}`,
    );
    // Backend status vocab is OK/WARNING/CRITICAL/null — normalize to STABLE/WARN/CRITICAL/SKIPPED.
    return { ...resp, rows: resp.rows.map((r) => ({ ...r, status: normalizeStatus(r.status) })) };
  }

  // Mock: derive each status from its value so the fixtures stay self-consistent.
  const classify = (r: FeatureStabilityRow): FeatureStabilityRow => ({
    ...r,
    status: classifyBand(r.value, warn, crit),
  });

  if (metric === "PSI") {
    return {
      segment,
      metric: "PSI",
      aggregateFlag: { fired: false, warnFeatureCount: 0, featuresInWarn: [] },
      rows: bySegmentPsi(segment).map(classify),
      notes: PSI_NOTES,
      cohortRange: "Feb 2024 – Jun 2025",
    };
  }
  const { rows, flag } = bySegmentCsi(segment);
  return {
    segment,
    metric: "CSI",
    aggregateFlag: flag,
    // Derive direction from each feature's own monthly trend (latest vs previous).
    rows: rows.map((r) => ({ ...classify(r), direction: computeDirection(r.trend) })),
    notes: CSI_NOTES,
    cohortRange: "Feb 2024 – Sep 2024 (reliable)",
  };
}

export function usePsi(segment: Segment, modelId: number = 1) {
  return useQuery({
    queryKey: ["psi", segment, modelId],
    queryFn: () => fetchStability("PSI", segment, modelId),
    enabled: modelId > 0,
  });
}

export function useCsi(segment: Segment, modelId: number = 1) {
  return useQuery({
    queryKey: ["csi", segment, modelId],
    queryFn: () => fetchStability("CSI", segment, modelId),
    enabled: modelId > 0,
  });
}

export function statusClasses(status: StabilityStatus): string {
  switch (status) {
    case "CRITICAL":
      return "bg-[color:var(--decline)]/15 text-[color:var(--decline)] border-[color:var(--decline)]/40";
    case "WARN":
      return "bg-amber-dim text-amber border-amber/40";
    case "STABLE":
      return "bg-[color:var(--approve)]/15 text-[color:var(--approve)] border-[color:var(--approve)]/40";
    case "SKIPPED":
    default:
      return "bg-muted/40 text-muted-foreground border-border";
  }
}

export function statusLabel(s: StabilityStatus): string {
  if (s === "STABLE") return "OK";
  return s;
}

export function directionLabel(d?: CsiDirection): string {
  if (d === "UP") return "↑ Up";
  if (d === "DOWN") return "↓ Down";
  if (d === "STABLE") return "— Stable";
  if (d === "MORE_PREDICTIVE") return "More predictive";
  if (d === "LESS_PREDICTIVE") return "Less predictive";
  return "—";
}
